import { PlayersToolbar } from "@/components/organisms/PlayersToolbar/PlayersToolbar";
import { canonicalUrl } from "@/config/canonical";
import { GAME_NAME } from "@/config/game";
import { useSeoCopy } from "@/config/seo";
import { HeroSection } from "@/containers/HeroSection";
import { trackFilter, trackQuickFilter } from "@/events/evolutionsMixpanel";
import { WingsCupBanner } from "@/features/WingsCup/WingsCup";
import { useGoBack } from "@/hooks/useGoBack";
import {
  quickFilterKeyFromType,
  useMyClubFilter,
} from "@/hooks/useMyClubFilter";
import { useOutsideClick } from "@/hooks/useOutsideClick";
import { useSticky } from "@/hooks/useSticky";
import {
  PlayersViewToggle,
  usePlayersView,
} from "@/pages/Players/PlayersViewToggle";
import { QuickFilters } from "@/pages/Players/QuickFilters";
import {
  evolutionsSelector,
  includedEvolutionsSelector,
  mustIncludeEvolutionsSelector,
  setActivePickerSelection,
  setIncludedEvolutions,
  setIncludedEvolutionsByState,
  setMustIncludeEvolutions,
} from "@/redux/evolutions/evolutionsSlice";
import {
  MetaRatingsPlayer,
  PlayerRole,
  clearFilter,
  fetchPlayers,
  ownedOrEvolvedSelector,
  playerFiltersAsQueryParamsSelector,
  playersSelector,
  updateFilter,
} from "@/redux/players/playersSlice";
import { AppDispatch } from "@/redux/store";
import { BREAKPOINTS } from "@/settings";
import type { PlayersSortByType } from "@repo/shared/api";
import type { EvolutionPickerSelection } from "@repo/shared/evolutions";
import { useTranslation } from "@repo/shared/i18n";
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Outlet, useLocation, useParams, useSearchParams } from "react-router";
import { useMediaQuery } from "usehooks-ts";

import useDebounce from "../../../hooks/useDebounce";
import { useUrlFilters } from "../../../hooks/useUrlFilters";
import { EvolutionsHubCards } from "../EvolutionsHub";
import { EvolutionsMobileNav } from "../EvolutionsMobileNav";
import { EvolutionsPickerBottomSheet } from "../EvolutionsPickerBottomSheet";
import { EvolutionsPlayersSection } from "../sections/EvolutionsPlayersSection";

export type ToolbarAction =
  | "playerRole"
  | "filters"
  | "includeExclude"
  | "downloadAssets"
  | "saveEvolution"
  | "none";

export const EvolutionsPage = () => {
  const { t } = useTranslation(["evolutions", "common"]);
  const playersSeo = useSeoCopy("evolutionsPlayers");
  const [scrollPastTopBar, refTopBar] = useSticky({
    triggerOn: "bottom",
    offset: 0,
  }) as [boolean, React.MutableRefObject<HTMLDivElement>];

  const dispatch = useDispatch<AppDispatch>();
  const includedEvolutions = useSelector(includedEvolutionsSelector);
  const mustIncludeEvolutions = useSelector(mustIncludeEvolutionsSelector);
  let {
    evolutionPlayers: currentPlayers,
    totalPages,
    playersStatus,
    playerFilters,
    playerRoles,
  } = useSelector(playersSelector);
  const [page, setPage] = useState(1);
  const { activePickerSelection, status: evolutionsStatus } =
    useSelector(evolutionsSelector);
  const [elementOpened, setElementOpened] = useState<ToolbarAction>("none");
  const [selectedPlayerRoles, setSelectedPlayerRoles] = useState<PlayerRole[]>(
    [],
  );
  const locationState = useLocation().state as { fromDetailedEvoPage: boolean };
  const [searchParams] = useSearchParams();
  const isTouchDevice = useMediaQuery(BREAKPOINTS.TOUCH_DEVICE.query);
  const isMobile = useMediaQuery(BREAKPOINTS.MOBILE.query);
  const stableQueryParams = useSelector(playerFiltersAsQueryParamsSelector);
  // Indexed landing page — a visitor from search has no history to go back to.
  const goBack = useGoBack();
  const ownedOrEvolvedFilters = useSelector(ownedOrEvolvedSelector);
  const { resourceId } = useParams();
  const hasFilters = useMemo(
    () => stableQueryParams.length > 0,
    [stableQueryParams],
  );
  const [searchTerm, setSearchTerm] = useState("");
  const { debouncedSearchTerm, errorMessage: searchTermErrorMessage } =
    useDebounce(searchTerm, {
      minLength: 3,
      contextForErrorMessage: t("common:playerName"),
    });

  const [view, setView] = usePlayersView("evolution-players-view");
  const [sortBy, setSortBy] = useState<PlayersSortByType>(
    (searchParams.get("sort") as PlayersSortByType) || "meta-rating",
  );
  const [player, setPlayer] = useState<MetaRatingsPlayer>();
  const [players, setPlayers] = useState<MetaRatingsPlayer[]>(currentPlayers);
  const [showEvolutionsPickerBottomSheet, setShowEvolutionsPickerBottomSheet] =
    useState(false);

  const refPlayerRole = useOutsideClick(() => {
    if (elementOpened === "playerRole") setElementOpened("none");
  });

  const refFilters = useOutsideClick(() => {
    if (elementOpened === "filters") {
      setElementOpened("none");
    }
  });

  const [playerRolesInitialized, setPlayerRolesInitialized] = useState(false);

  const playerRoleIds = useMemo(
    () => selectedPlayerRoles.map((r) => r.id),
    [selectedPlayerRoles],
  );

  useUrlFilters({
    playerFilters,
    sort: sortBy,
    ownedOrEvolvedFilters,
    stableQueryParams,
    playerRoleIds,
    readyToSync: playerRolesInitialized && !resourceId,
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleSearch = (searchTerm: string) => {
    if (sortBy === "date-added") {
      setPage(1);
      setSortBy("meta-rating");
    }
    setSearchTerm(searchTerm);
  };

  useEffect(() => {
    return () => {
      if (locationState?.fromDetailedEvoPage) {
        dispatch(setIncludedEvolutionsByState("available"));
        dispatch(clearFilter("max-chain-length"));
      }
    };
  }, []);

  useEffect(() => {
    if (playerRoles.length === 0) return;

    const idsParam = searchParams.get("player-role-ids");
    const legacyId = searchParams.get("player-role-id");
    const ids: number[] = [];
    if (idsParam) {
      ids.push(
        ...idsParam
          .split(",")
          .map((s) => parseInt(s, 10))
          .filter((n) => !Number.isNaN(n)),
      );
    } else if (legacyId) {
      const n = parseInt(legacyId, 10);
      if (!Number.isNaN(n)) ids.push(n);
    }

    // Same selection must keep the same array — the fetch effect below keys on
    // its identity, and a fresh [] repeats the request.
    setSelectedPlayerRoles((prev) => {
      const next = playerRoles.filter((r) => ids.includes(r.id));
      const unchanged =
        prev.length === next.length &&
        prev.every((r, i) => r.id === next[i].id);
      return unchanged ? prev : next;
    });
    setPlayerRolesInitialized(true);
  }, [playerRoles]);

  const handleToolbarAction = (action: ToolbarAction) => {
    if (action === "includeExclude" && !isTouchDevice) {
      setShowEvolutionsPickerBottomSheet(true);
      return;
    }
    setElementOpened(elementOpened === action ? "none" : action);
  };

  useEffect(() => {
    setPlayers(currentPlayers);
  }, [currentPlayers]);

  useEffect(() => {
    setPlayer(
      players.find(
        (p) =>
          p.eaPlayerId === +resourceId ||
          (p.resourceId === +resourceId && !p.eaPlayerId),
      ),
    );
  }, [players, resourceId]);

  useEffect(() => {
    setPage(1);
  }, [stableQueryParams, sortBy, selectedPlayerRoles]);

  useEffect(() => {
    // Wait for evoIds; firing earlier asks unfiltered and gets discarded.
    // "rejected" passes so a failed evolutions fetch still shows a list.
    if (evolutionsStatus === "idle" || evolutionsStatus === "pending") return;

    const roleIds = selectedPlayerRoles.map((r) => r.id);
    dispatch(
      fetchPlayers({
        page,
        sort: sortBy,
        myClub: ownedOrEvolvedFilters?.category === "Owned",
        myEvolvedPlayers: ownedOrEvolvedFilters?.category === "Evolved",
        activeSquad: ownedOrEvolvedFilters?.category === "ActiveSquad",
        ...(roleIds.length > 0
          ? { playerRoleIds: roleIds }
          : { playerRoleId: undefined }),
        queryParams: stableQueryParams,
        evolutions: true,
        evoIds: [...includedEvolutions, ...mustIncludeEvolutions],
        mustIncludeEvoIds: mustIncludeEvolutions,
        searchText: debouncedSearchTerm,
        requestedFromPage: "evolutions",
      }),
    );
  }, [
    dispatch,
    evolutionsStatus,
    page,
    sortBy,
    selectedPlayerRoles,
    stableQueryParams,
    includedEvolutions,
    mustIncludeEvolutions,
    ownedOrEvolvedFilters,
    debouncedSearchTerm,
  ]);

  const handleSelectPlayerRoles = (roles: PlayerRole[]) => {
    setSelectedPlayerRoles(roles);
    const filters = stableQueryParams
      .map((qp) => Object.keys(qp ?? {})[0])
      .filter(Boolean);

    trackFilter({ subsection: "Players", filters: [...filters, "playerRole"] });
  };

  const handleMyClubClicked = useMyClubFilter(
    (f) => {
      dispatch(updateFilter(f));
      if (sortBy === "date-added") {
        setSortBy("meta-rating");
      }
    },
    {
      onFilterApplied: (type) =>
        trackQuickFilter({
          subsection: "Players",
          filters: quickFilterKeyFromType(type),
        }),
      origin: { section: "Evolutions", subsection: "Players" },
      upgradeTracking: {
        section: "Evolutions",
        subsection: "Players",
        context: "filter",
      },
    },
  );

  const handleSelectActiveFilter = (filter: EvolutionPickerSelection) => {
    dispatch(setActivePickerSelection(filter));
  };

  const onFiltersUpdated = () => {
    const filters = stableQueryParams
      .map((qp) => Object.keys(qp ?? {})[0])
      .filter(Boolean);

    trackFilter({
      subsection: "Players",
      filters,
    });
  };

  return resourceId ? (
    <Outlet context={player} />
  ) : (
    <main className="min-h-screen mx-auto bg-background">
      <>
        <HeroSection
          helmetTitle={playersSeo.title}
          pageTitle={t("players.pageTitle", { game: GAME_NAME })}
          helmetDescription={playersSeo.description}
          subtitle={playersSeo.subtitle}
          helmetCanonicalLink={canonicalUrl("/evolutions/players")}
          back={goBack}
          alignCenter={false}
          shouldBeInTheDocumentFlow
        >
          <div className="my-8 hidden mobile:block">
            <EvolutionsHubCards activeEvoCard="players" />
          </div>
        </HeroSection>
        <EvolutionsMobileNav active="players" />
        <div ref={refTopBar} />
        <PlayersToolbar
          searchTerm={searchTerm}
          setSearchTerm={handleSearch}
          handleToolbarAction={handleToolbarAction}
          isMobile={isMobile}
          hasFilters={hasFilters}
          sortBy={sortBy}
          setSortBy={(v) => {
            setSortBy(v);
          }}
          selectedPlayerRoles={selectedPlayerRoles}
          includedEvolutionsCount={[...includedEvolutions].length}
          pageType="evolutions"
          viewToggle={<PlayersViewToggle value={view} onChange={setView} />}
          quickFilters={
            <QuickFilters
              onMyClubClicked={handleMyClubClicked}
              onFiltersChanged={() => {}}
            />
          }
        />
        <EvolutionsPlayersSection
          onFiltersUpdated={onFiltersUpdated}
          elementOpened={elementOpened}
          playersStatus={playersStatus}
          players={players}
          isTouchDevice={isTouchDevice}
          sortBy={sortBy}
          selectedPlayerRoles={selectedPlayerRoles}
          playerFilters={playerFilters}
          scrollPastTopBar={scrollPastTopBar}
          handleToolbarAction={handleToolbarAction}
          hasFilters={hasFilters}
          playerRoles={playerRoles}
          handleSelectPlayerRoles={handleSelectPlayerRoles}
          multiSelectPlayerRoles
          refPlayerRole={refPlayerRole}
          refFilters={refFilters}
          handleMyClubClicked={handleMyClubClicked}
          page={page}
          totalPages={totalPages}
          setPage={setPage}
          setSortBy={setSortBy}
          searchTermErrorMessage={searchTermErrorMessage}
          view={view}
          afterSecondRow={
            <WingsCupBanner unit="evo-players" className="col-span-full" />
          }
        />
      </>
      {showEvolutionsPickerBottomSheet && (
        <EvolutionsPickerBottomSheet
          isOpen={showEvolutionsPickerBottomSheet}
          onClose={() => setShowEvolutionsPickerBottomSheet(false)}
          initialIncludedEvolutions={includedEvolutions}
          initialMustIncludeEvolutions={mustIncludeEvolutions}
          onApply={(data) => {
            trackFilter({ subsection: "Players", filters: ["evolutions"] });
            dispatch(setIncludedEvolutions(data.includedEvolutions));
            dispatch(setMustIncludeEvolutions(data.mustIncludeEvolutions));
          }}
          activeFilter={activePickerSelection}
          handleSelectActiveFilter={handleSelectActiveFilter}
        />
      )}
    </main>
  );
};

export default EvolutionsPage;
